import clsx from 'clsx';
import {createContext, ReactNode, RefObject, type HTMLAttributes} from 'react';
import {useContext} from 'react';
import styles from './Tabs.module.scss';

interface TabsState {
  tab: string | null;
  setTab: (tab: string | null) => void;
}

const TabsContext = createContext<TabsState>({
  tab: null,
  setTab: () => {},
});

export function Tabs({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx(className, styles.tabs)} {...props} />;
}

export interface TabGroupProps extends TabsState {
  children: ReactNode;
}

export function TabGroup({children, ...rest}: TabGroupProps) {
  return <TabsContext.Provider value={rest}>{children}</TabsContext.Provider>;
}

export interface TabProps extends HTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  forwardRef?: RefObject<HTMLButtonElement>;
  tab: string;
}

export function Tab({className, tab, forwardRef, ...props}: TabProps) {
  const {tab: currentTab, setTab} = useContext(TabsContext);
  const active = tab === currentTab;

  return (
    <button
      ref={forwardRef}
      onClick={() => setTab(active ? null : tab)}
      className={clsx(styles.tab, active && styles.active, className)}
      {...props}
    />
  );
}

export interface TabLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
  disabled?: boolean;
  href?: string;
  target?: string;
}

export function TabLink({className, href, disabled, target, ...props}: TabLinkProps) {
  return (
    <a
      className={clsx(
        styles.tab,
        (!href || disabled) && styles.disabled,
        className,
      )}
      href={href}
      target={target}
      {...props}
    />
  );
}

export function Space() {
  return <div className={styles.space} />;
}
